/**
 * Browser mirror of inc/mapping-engine.php, used for the live preview.
 * Prices and the final design are always re-validated on the server.
 */
import type { ConfiguratorOption, ConfiguratorPatch, ConfiguratorProfile, Condition, Resolved, Selections } from './types';

export function asString(value: unknown): string {
  if (typeof value === 'boolean') return value ? '1' : '0';
  const text = String(value ?? '');
  if (text === 'true') return '1';
  if (text === 'false') return '0';
  return text;
}

export function conditionsMet(conditions: Condition[], selections: Selections): boolean {
  return (conditions ?? []).every((condition) => {
    const actual = asString(selections[condition.option]);
    const expected = condition.value;
    switch (condition.operator) {
      case 'not_equals':
        return actual !== asString(expected);
      case 'in': {
        const list = Array.isArray(expected) ? expected : String(expected ?? '').split(',').map((v) => v.trim());
        return list.map(String).includes(actual);
      }
      default:
        return actual === asString(expected);
    }
  });
}

export function findChoice(option: ConfiguratorOption, value: unknown) {
  return option.choices.find((choice) => choice.value === String(value ?? ''));
}

export function resolveSelections(profile: ConfiguratorProfile, raw: Selections, requiredMessage = (label: string) => `Choose "${label}".`): Resolved {
  const clean: Selections = {};
  for (const option of profile.options) {
    const value = raw[option.id];
    if (option.type === 'checkbox') {
      clean[option.id] = value === undefined ? Boolean(option.default) : value === true || value === '1' || value === 'true';
    } else if (option.type === 'text') {
      let text = value === undefined ? String(option.default ?? '') : String(value).slice(0, option.max_length ?? 40);
      if (option.transform === 'uppercase') text = text.toUpperCase();
      if (option.transform === 'lowercase') text = text.toLowerCase();
      clean[option.id] = text;
    } else {
      let chosen = value === undefined ? '' : String(value);
      if (!chosen || !findChoice(option, chosen)) {
        const fallback = String(option.default ?? '');
        chosen = findChoice(option, fallback) ? fallback : option.required ? '' : option.choices[0]?.value ?? '';
      }
      clean[option.id] = chosen;
    }
  }

  const selections: Selections = {};
  const visible: string[] = [];
  const errors: string[] = [];
  for (const option of profile.options) {
    if (!conditionsMet(option.show_if, selections)) continue;
    visible.push(option.id);
    selections[option.id] = clean[option.id];
    if (option.required && (clean[option.id] === '' || clean[option.id] === false)) {
      errors.push(requiredMessage(option.label));
    }
  }
  return { selections, visible, errors };
}

export function buildPatches(profile: ConfiguratorProfile, resolved: Resolved): ConfiguratorPatch[] {
  const options = new Map(profile.options.map((option) => [option.id, option]));
  const patches: ConfiguratorPatch[] = [];
  for (const rule of profile.mappings) {
    if (!conditionsMet(rule.when, resolved.selections)) continue;
    for (const action of rule.actions) {
      const option = action.option ? options.get(action.option) : undefined;
      if (action.option && (!option || !(action.option in resolved.selections))) continue;
      const selected = option ? resolved.selections[action.option] : action.value ?? '';
      const choice = option && option.type !== 'checkbox' && option.type !== 'text' ? findChoice(option, selected) : undefined;
      const layerId = action.layerId;
      switch (action.type) {
        case 'setProductColor':
          patches.push({ type: 'setProductColor', colorIndex: Math.max(0, parseInt(choice ? choice.value : String(selected), 10) || 0) });
          break;
        case 'setText':
          patches.push({ layerId, patch: { text: choice ? choice.label : String(selected) } });
          break;
        case 'setColor': {
          const color = (choice ? choice.color || choice.value : String(selected)).replace(/^#/, '');
          // Designer items keep CSS colors ("#rrggbb"); a bare hex renders black.
          if (/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(color)) patches.push({ layerId, patch: { color: `#${color.toLowerCase()}` } });
          break;
        }
        case 'setFont': {
          const font = choice ? choice.value : String(selected);
          if (font) patches.push({ layerId, patch: { fontFamily: font } });
          break;
        }
        case 'setSrc': {
          const src = option ? choice?.image ?? '' : String(selected);
          if (src) patches.push({ layerId, patch: { src } });
          break;
        }
        case 'setVisibility': {
          const visible = option && option.type === 'checkbox'
            ? selected === true
            : choice ? true : selected === true || selected === '1' || selected === 'true';
          patches.push({ layerId, patch: { visible } });
          break;
        }
      }
    }
  }
  return patches;
}

/** Layer ids (and fields) any rule of the profile can touch. */
export function targetedFields(profile: ConfiguratorProfile): Map<string, Set<string>> {
  const fields: Record<string, string[]> = {
    setText: ['text'],
    setColor: ['color', 'typography'],
    setSrc: ['src'],
    setVisibility: ['visible'],
    setFont: ['fontFamily'],
  };
  const map = new Map<string, Set<string>>();
  for (const rule of profile.mappings) {
    for (const action of rule.actions) {
      if (!fields[action.type] || !action.layerId) continue;
      if (!map.has(action.layerId)) map.set(action.layerId, new Set());
      for (const field of fields[action.type]) map.get(action.layerId)!.add(field);
    }
  }
  return map;
}

type TypographyFill = { type?: string; color?: string };
type Typography = { appearance?: { fill?: TypographyFill; [key: string]: unknown }; [key: string]: unknown };

/**
 * Fields to write on a layer for a patch. Typography Pro text paints from
 * `typography.appearance.fill`, so a color change has to land there too.
 */
export function expandPatch(item: Record<string, unknown>, patch: Record<string, unknown>): Record<string, unknown> {
  const typography = item.typography as Typography | undefined;
  const fill = typography?.appearance?.fill;
  if (typeof patch.color !== 'string' || !fill) return patch;
  return {
    ...patch,
    typography: { ...typography, appearance: { ...typography!.appearance, fill: { ...fill, type: 'solid', color: patch.color } } },
  };
}

/** Price shown next to a choice (the server re-prices at checkout and charges surcharges only, never discounts). */
export function choicePrice(option: ConfiguratorOption, value: unknown): number {
  const price = option.type === 'checkbox'
    ? (value === true ? option.price ?? 0 : 0)
    : findChoice(option, value)?.price ?? 0;
  return Number.isFinite(price) && price > 0 ? price : 0;
}
